import {CommonModule} from '@angular/common'; // Import CommonModule
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core'; // Removed ApplicationRef
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {Subject} from 'rxjs';

@Component({
    selector: 'app-wysiwyg',
    templateUrl: './wysiwyg.component.html',
    styleUrls: ['./wysiwyg.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule]
})
export class WysiwygComponent implements OnInit, AfterViewInit { // Removed OnChanges
    @Input() public content: string;
    @Input() public disabled = false;
    @Input() public required = false;
    @Input() public maxLength: number = null;
    @Output() public contentChange = new EventEmitter<string>(); // Changed type to string
    @Output() wordCountUpdate = new EventEmitter<{ words: number, characters: number }>();
    @Output() editorReady = new EventEmitter<void>(); // Add new Output

    private changeDetector = inject(ChangeDetectorRef);
    private sanitizer = inject(DomSanitizer);
    editorContainerRef = inject(ViewContainerRef); // Restore direct injection
    isLoading = true; // Add loading state

    public isLayoutReady = false;
    public config: any = {};
    private editorInstance: any = null;

    ngOnInit(): void {
        this.content = this.content || '';
    }

    async ngAfterViewInit(): Promise<void> {
        await this.loadEditor();
    }

    async loadEditor(): Promise<void> {
        try {
            // Dynamically import CKEditor core and plugins
            const editorBuild = await import('ckeditor5');
            const {CKEditorComponent} = await import('@ckeditor/ckeditor5-angular');

            const ClassicEditor = editorBuild.ClassicEditor;
            const plugins = [
                editorBuild.AccessibilityHelp,
                editorBuild.Autoformat,
                editorBuild.Autosave,
                editorBuild.Bold,
                editorBuild.Essentials,
                editorBuild.Italic,
                editorBuild.List,
                editorBuild.Paragraph,
                editorBuild.PasteFromOffice,
                editorBuild.SelectAll,
                editorBuild.Undo,
                editorBuild.WordCount,
            ];

            this.config = {
                toolbar: {
                    items: ['undo', 'redo', '|', 'bold', 'italic', '|', 'bulletedList', 'numberedList'],
                    shouldNotGroupWhenFull: false
                },
                plugins: plugins,
                language: 'fr',
                placeholder: 'Tapez ou collez votre contenu ici!',
                wordCount: {
                    onUpdate: (stats: { words: number; characters: number; }) => {
                        this.wordCountUpdate.emit(stats);
                    }
                }
            };

            this.editorContainerRef.clear();
            const componentRef = this.editorContainerRef.createComponent(CKEditorComponent);

            componentRef.instance.editor = ClassicEditor;
            componentRef.instance.config = this.config;
            componentRef.instance.data = this.content || '';
            componentRef.instance.disabled = this.disabled;

            componentRef.instance.ready.subscribe((editor: any) => {
                this.editorInstance = editor;
                this.isLoading = false;
                this.isLayoutReady = true;
                this.changeDetector.markForCheck();
                this.editorReady.emit();

                if (this.maxLength) {
                    this.editorInstance.editing.view.document.on('keydown', (evt, data) => {
                        const html = this.editorInstance.getData() || '';
                        const plainText = this.stripHtml(html);
                        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
                        const finish = (this.maxLength - plainText.length) <= 0;
                        if (finish && !allowedKeys.includes(data.domEvent.key)) {
                            data.preventDefault();
                            evt.stop();
                        }
                    });
                    this.managePast();
                }
            });

            componentRef.instance.change.subscribe((event: { editor: any }) => {
                this.onContentChange(event.editor);
            });

            componentRef.onDestroy(() => {
                this.editorInstance = null; // Clear instance on destroy
            });

        } catch (error) {
            console.error('[loadEditor] Error loading CKEditor dynamically:', error);
            this.isLoading = false;
            this.isLayoutReady = false;
            this.changeDetector.markForCheck();
        }
    }

// Updated to receive the editor instance directly
    onContentChange(editorInstance: any): void {
        if (editorInstance) {
            const rawHtml: string = editorInstance.getData(); // Get raw HTML string
            this.contentChange.emit(rawHtml); // Emit raw HTML string
            // Note: Word count is handled by the config.onUpdate now
        }
    }

    /**
     * Clears the content of the CKEditor instance.
     * This method can be called from a parent component using ViewChild.
     */
    public clearContent(): void {
        if (this.editorInstance) {
            this.editorInstance.setData('');
        } else {
            console.warn('WysiwygComponent: clearContent called before editor instance is ready.');
        }
    }

    private managePast() {
        this.editorInstance.editing.view.document.on('paste', (evt, data) => {
            const selection = window.getSelection();
            let selectionLenght = 0;
            // manage paste if some text are selected in area remove charact who will be deleted of count
            if (selection && selection.rangeCount > 0 && selection.toString().length > 0) {
                selectionLenght = this.stripHtml(selection.toString()).length;
            }

            const clipboardDataOriginal = data.dataTransfer.getData('text/plain') || '';
            const clipboardData = this.stripHtml(clipboardDataOriginal);
            let html = this.editorInstance.getData() || '';
            const currentText = this.stripHtml(html);
            const allowedLength = this.maxLength - currentText.length + selectionLenght;
            if (allowedLength <= 0) {
                data.preventDefault();
                evt.stop();
            } else if (clipboardData.length > allowedLength) {
                const content = this.stripHtml(clipboardData);
                // remove sometext to respect the max size
                const truncatedText = content.slice(0, allowedLength);
                data.preventDefault();
                evt.stop();
                // insert text
                this.editorInstance.model.change(writer => {
                    const viewFragment = this.editorInstance.data.processor.toView(truncatedText);
                    const modelFragment = this.editorInstance.data.toModel(viewFragment);
                    this.editorInstance.model.insertContent(modelFragment);
                });
            }
        });
    }

    private stripHtml(html: string): string {
        const div = document.createElement('div');
        div.innerHTML = html;
        const withoutHtml = div.textContent || div.innerText || '';
        const cleanNoReturn = withoutHtml.replace(/[\r\n\t]+/g, ' ');
        const cleanNoInvisible = cleanNoReturn.replace(/[\r\n\t\u200B\u200C\u200D\u00A0\u202F]+/g, ' ');
        return cleanNoInvisible.replace(/\s+/g, ' ').trim();
    }
}
